import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useGameStore } from '../store/gameStore'
import Button from '../components/Button'
import { Mail, Lock, User, Save } from 'lucide-react'
import { useI18n } from '../i18n/I18nProvider'

function Auth() {
  const { t } = useI18n()
  const [searchParams] = useSearchParams()
  const initialMode = searchParams.get('mode') || 'signin'
  const shouldSave = searchParams.get('save') === 'true'
  
  const [mode, setMode] = useState(initialMode) // signin, signup, reset
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const signIn = useAuthStore((state) => state.signIn)
  const signUp = useAuthStore((state) => state.signUp)
  const resetPassword = useAuthStore((state) => state.resetPassword)
  const seeds = useGameStore((state) => state.seeds)
  const currentPlant = useGameStore((state) => state.currentPlant)
  
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password)
        if (error) throw error
        navigate('/garden')
      } else if (mode === 'signup') {
        if (!username) {
          throw new Error('Le nom d\'utilisateur est requis')
        }
        const { error } = await signUp(email, password, username)
        if (error) throw error
        setMessage('Compte créé ! Vérifiez votre email pour confirmer votre inscription.')
      } else if (mode === 'reset') {
        const { error } = await resetPassword(email)
        if (error) throw error
        setMessage(t('auth.resetEmailSent'))
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-3xl font-bold text-center text-green-800 mb-8">
            {mode === 'signin' && t('auth.signIn')}
            {mode === 'signup' && (shouldSave ? t('auth.saveProgress') : t('auth.signUp'))}
            {mode === 'reset' && t('auth.resetPassword')}
          </h2>

          {shouldSave && mode === 'signup' && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Save size={20} className="text-green-600" />
                <span className="font-bold text-green-800">Votre progression</span>
              </div>
              <ul className="text-sm text-gray-700 space-y-1">
                {currentPlant && <li>✓ Une plante en cours de croissance</li>}
                {seeds > 0 && <li>✓ {seeds} graine{seeds > 1 ? 's' : ''}</li>}
              </ul>
              <p className="text-xs text-gray-600 mt-2">
                Créez un compte gratuit pour ne jamais perdre votre progression !
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.usernameLabel')}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder={t('auth.usernamePlaceholder')}
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                />
              </div>
            </div>

            {mode !== 'reset' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.passwordLabel')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder={t('auth.passwordPlaceholder')}
                    required
                    minLength={6}
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={loading}
            >
              {mode === 'signin' && t('auth.signInButton')}
              {mode === 'signup' && t('auth.signUpButton')}
              {mode === 'reset' && t('auth.sendLinkButton')}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {mode === 'signin' && (
              <>
                <button
                  onClick={() => setMode('reset')}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  {t('auth.forgotPassword')}
                </button>
                <div className="text-sm text-gray-600">
                  {t('auth.noAccount')}{' '}
                  <button
                    onClick={() => setMode('signup')}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    {t('auth.signUpButton')}
                  </button>
                </div>
              </>
            )}

            {mode === 'signup' && (
              <div className="text-sm text-gray-600">
                {t('auth.hasAccount')}{' '}
                <button
                  onClick={() => setMode('signin')}
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  {t('auth.signInButton')}
                </button>
              </div>
            )}

            {mode === 'reset' && (
              <button
                onClick={() => setMode('signin')}
                className="text-sm text-green-600 hover:text-green-700"
              >
                {t('auth.backToSignIn')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Auth
